/**
 * Shared Retry Utilities
 * 
 * Provides configurable retry logic with exponential backoff and jitter
 * for handling transient failures across the application.
 * 
 * @module shared/retry
 */

/**
 * Options for retry behavior
 */
export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxAttempts?: number;
  /** Initial delay in milliseconds (default: 100) */
  baseDelayMs?: number;
  /** Maximum delay in milliseconds (default: 5000) */
  maxDelayMs?: number;
  /** Exponential backoff multiplier (default: 2) */
  backoffMultiplier?: number;
  /** Whether to add jitter to delays (default: true) */
  jitter?: boolean;
  /** Predicate to determine if error is retryable (default: all errors retryable) */
  isRetryable?: (error: unknown) => boolean;
  /** Callback invoked before each retry with attempt number and error */
  onRetry?: (attempt: number, error: unknown) => void;
}

/**
 * Default retry options
 */
const DEFAULT_RETRY_OPTIONS: Required<Omit<RetryOptions, 'onRetry'>> = {
  maxAttempts: 3,
  baseDelayMs: 100,
  maxDelayMs: 5000,
  backoffMultiplier: 2,
  jitter: true,
  isRetryable: () => true,
};

/**
 * Execute an async operation with retry logic and exponential backoff.
 * 
 * @param fn - Async function to execute
 * @param options - Retry configuration options
 * @returns Result of the function execution
 * @throws Last error encountered if all retries are exhausted
 * 
 * @example
 * ```typescript
 * const data = await retryWithBackoff(
 *   async () => await fetch('/api/data'),
 *   { maxAttempts: 3, baseDelayMs: 200 }
 * );
 * ```
 * 
 * @example
 * ```typescript
 * // Custom retry predicate for specific errors
 * const result = await retryWithBackoff(
 *   async () => await apiCall(),
 *   {
 *     maxAttempts: 5,
 *     isRetryable: (err) => {
 *       if (err instanceof Error) {
 *         return err.message.includes('timeout') || err.message.includes('ECONNRESET');
 *       }
 *       return false;
 *     },
 *     onRetry: (attempt, err) => console.warn(`Retry attempt ${attempt}:`, err)
 *   }
 * );
 * ```
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...options };
  
  let attempt = 0;
  let lastError: unknown;
  let delay = opts.baseDelayMs;

  while (attempt <= opts.maxAttempts) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Check if error is retryable
      if (!opts.isRetryable(error)) {
        throw error;
      }

      // Check if we've exhausted retries
      if (attempt >= opts.maxAttempts) {
        throw error;
      }

      // Invoke retry callback if provided
      if (options.onRetry) {
        options.onRetry(attempt + 1, error);
      }

      // Calculate delay with optional jitter
      const actualDelay = opts.jitter 
        ? delay + Math.random() * delay * 0.5  // Add up to 50% jitter
        : delay;

      // Wait before next attempt
      await sleep(Math.min(actualDelay, opts.maxDelayMs));

      // Increase delay for next iteration (exponential backoff)
      delay = delay * opts.backoffMultiplier;
      attempt++;
    }
  }

  // Should never reach here, but TypeScript needs this
  throw lastError ?? new Error('Retry loop exhausted without error');
}

/**
 * Create a retry wrapper function with pre-configured options.
 * Useful for creating reusable retry strategies.
 * 
 * @param options - Default retry options to apply
 * @returns Function that accepts an operation and applies retry logic
 * 
 * @example
 * ```typescript
 * const retryApi = createRetryWrapper({
 *   maxAttempts: 5,
 *   baseDelayMs: 200,
 *   isRetryable: (err) => err instanceof NetworkError
 * });
 * 
 * const data = await retryApi(() => fetch('/api/data'));
 * ```
 */
export function createRetryWrapper(options: RetryOptions = {}) {
  return async function retry<T>(fn: () => Promise<T>): Promise<T> {
    return retryWithBackoff(fn, options);
  };
}

/**
 * Sleep utility for async delays.
 * 
 * @param ms - Milliseconds to sleep
 * @returns Promise that resolves after the delay
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Check if an error represents a transient network failure.
 * Useful as a default isRetryable predicate for network operations.
 * 
 * @param error - Error to check
 * @returns True if error appears to be a transient network issue
 * 
 * @example
 * ```typescript
 * const data = await retryWithBackoff(
 *   async () => await fetch('/api/data'),
 *   { isRetryable: isTransientNetworkError }
 * );
 * ```
 */
export function isTransientNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) {
    // Fetch throws TypeError for network failures
    return error.message.includes('fetch') || error.message.includes('network');
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('timeout') ||
      message.includes('econnreset') ||
      message.includes('econnrefused') ||
      message.includes('etimedout') ||
      message.includes('socket hang up') ||
      message.includes('network') ||
      message.includes('fetch failed')
    );
  }

  return false;
}

/**
 * Check if an HTTP response status code is retryable.
 * 
 * @param status - HTTP status code
 * @returns True if status indicates a transient error worth retrying
 * 
 * @example
 * ```typescript
 * const response = await retryWithBackoff(
 *   async () => {
 *     const res = await fetch('/api/data');
 *     if (!res.ok && !isRetryableHttpStatus(res.status)) {
 *       throw new Error(`HTTP ${res.status}`);
 *     }
 *     return res;
 *   },
 *   { isRetryable: (err) => err instanceof Error && /HTTP 5\d\d/.test(err.message) }
 * );
 * ```
 */
export function isRetryableHttpStatus(status: number): boolean {
  // Retry on server errors and rate limiting
  return (
    status === 408 || // Request Timeout
    status === 429 || // Too Many Requests
    status === 500 || // Internal Server Error
    status === 502 || // Bad Gateway
    status === 503 || // Service Unavailable
    status === 504    // Gateway Timeout
  );
}
